import { StripeConnectedAccount } from '@umss/shared/data-access';
import { humanizeFields } from './stripe-requirements.helper';

export type StatusSeverity =
  | 'success'
  | 'info'
  | 'info-warning'
  | 'warning'
  | 'error'
  | 'action-required';

export type StatusAction =
  | 'NONE'
  | 'WAIT'
  | 'CREATE_ACCOUNT'
  | 'ADD_BANK_ACCOUNT'
  | 'OPEN_STRIPE_ONBOARDING'
  | 'CONTACT_SUPPORT';

export interface StatusInfo {
  severity: StatusSeverity;
  title: string;
  message: string;
  action: StatusAction;
  actionLabel?: string;
  dismissable?: boolean;
}

export function getStatusInfo(data: StripeConnectedAccount | null): StatusInfo {
  if (!data || !data.hasAccount) {
    return {
      severity: 'action-required',
      title: 'Set Up Your Payment Account',
      message: 'You need to create a payment account to receive earnings.',
      action: 'CREATE_ACCOUNT'
    };
  }

  if (data.status === 'Disabled') {
    return {
      severity: 'error',
      title: 'Payment Account Disabled',
      message:
        'Your payment account has been permanently disabled. Please contact support for assistance.',
      action: 'CONTACT_SUPPORT'
    };
  }

  // The backend exposes two independent, authoritative flags:
  // - `taxInfoVerified`: W-9/identity verified (ignores bank and restriction)
  // - `hasBankAccount`: a payout destination is linked in Stripe
  const needsW9 = !data.taxInfoVerified;
  const needsBank = !data.hasBankAccount;

  if (data.status === 'Restricted') {
    if (needsW9) {
      return {
        severity: 'error',
        title: 'Payouts Suspended',
        message:
          'Your payouts are suspended because your tax information (IRS Form W-9) is incomplete. Please complete verification on Stripe to restore your account.',
        action: 'OPEN_STRIPE_ONBOARDING',
        actionLabel: 'Complete W-9 on Stripe'
      };
    }

    if (needsBank) {
      return {
        severity: 'error',
        title: 'Payouts Suspended — Bank Account Required',
        message:
          'Your payouts are suspended because no bank account is linked. Please add your bank account in the section below to restore your payouts.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    // Both W-9 and bank are complete — restriction is for another reason.
    if (data.disabledReason === 'rejected.terms_of_service') {
      return {
        severity: 'error',
        title: 'Terms of Service Required',
        message:
          'Your payment account requires you to re-accept the Stripe terms of service. Please re-open verification to continue.',
        action: 'OPEN_STRIPE_ONBOARDING',
        actionLabel: 'Re-accept Terms on Stripe'
      };
    }

    return {
      severity: 'error',
      title: 'Payment Account Restricted',
      message:
        'Your payment account is restricted. Please contact support for assistance.',
      action: 'CONTACT_SUPPORT'
    };
  }

  if (data.status === 'RequiresInformation') {
    const isPastDue = data.pastDue.length > 0;

    if (needsW9) {
      return {
        severity: isPastDue ? 'error' : 'warning',
        title: isPastDue
          ? 'Action Required — Overdue Verification'
          : 'Additional Information Needed',
        message: isPastDue
          ? 'Your tax information (IRS Form W-9) is overdue. Your payouts may be suspended if not provided soon.'
          : 'Stripe requires your tax information (IRS Form W-9) to keep your account active. Please complete verification.',
        action: 'OPEN_STRIPE_ONBOARDING',
        actionLabel: 'Complete W-9 on Stripe'
      };
    }

    if (needsBank) {
      return {
        severity: isPastDue ? 'error' : 'warning',
        title: 'Bank Account Required',
        message:
          'A bank account is required to receive payouts. Please link your bank account in the section below.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    return {
      severity: 'info',
      title: 'Account Under Review',
      message:
        "Your payment account is being reviewed by Stripe. This usually takes a few minutes. We'll update you when it's ready.",
      action: 'WAIT'
    };
  }

  // `UnderReview` means the info was already submitted and Stripe is analyzing
  // it — don't ask the user to "complete W-9" again.
  if (data.status === 'UnderReview') {
    if (needsBank) {
      return {
        severity: 'warning',
        title: 'Complete Your Account Setup',
        message: needsW9
          ? 'Your tax information is being reviewed by Stripe. A bank account is also still needed to finish setting up your account. Please add your bank account below.'
          : 'A bank account is still needed to finish setting up your account. Please add your bank account below.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    if (needsW9) {
      return {
        severity: 'info',
        title: 'Verification in Review',
        message:
          "Your tax information is being reviewed by Stripe. We'll update you when it's ready.",
        action: 'WAIT'
      };
    }

    return {
      severity: 'info',
      title: 'Account Under Review',
      message:
        "Your payment account is being reviewed by Stripe. This usually takes a few minutes. We'll update you when it's ready.",
      action: 'WAIT'
    };
  }

  if (data.status === 'Pending') {
    if (needsW9) {
      return {
        severity: 'warning',
        title: 'Complete Your Account Setup',
        message:
          'Your tax information (IRS Form W-9) is still needed to finish setting up your account. Please complete verification.',
        action: 'OPEN_STRIPE_ONBOARDING',
        actionLabel: 'Complete W-9 on Stripe'
      };
    }

    if (needsBank) {
      return {
        severity: 'warning',
        title: 'Complete Your Account Setup',
        message:
          'A bank account is still needed to finish setting up your account. Please add your bank account below.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    return {
      severity: 'info',
      title: 'Account Under Review',
      message:
        "Your payment account is being reviewed by Stripe. This usually takes a few minutes. We'll update you when it's ready.",
      action: 'WAIT'
    };
  }

  // `status` is already the authoritative signal (see model doc comment) — don't gate on
  // the separate `onboardingCompleted` flag, or the two can disagree and fall through.
  if (data.status === 'Verified') {
    if (data.eventuallyDue.length > 0) {
      const taxFields = data.eventuallyDue.filter(
        (f) => f !== 'external_account'
      );
      const hasTaxFields = taxFields.length > 0;
      return {
        severity: 'info-warning',
        title: 'Payment Account Active',
        message: hasTaxFields
          ? `Your account is active and you can receive payments. However, Stripe will eventually require your tax information (IRS Form W-9): ${humanizeFields(taxFields)}. We recommend completing this now to avoid future interruptions.`
          : `Your account is active and you can receive payments. However, Stripe will eventually require: ${humanizeFields(data.eventuallyDue)}.`,
        action: hasTaxFields ? 'OPEN_STRIPE_ONBOARDING' : 'ADD_BANK_ACCOUNT',
        actionLabel: hasTaxFields ? 'Complete W-9 on Stripe' : undefined,
        dismissable: true
      };
    }
    return {
      severity: 'success',
      title: 'Payment Account Ready',
      message:
        "Your payment account is fully set up. You're all set to receive earnings!",
      action: 'NONE'
    };
  }

  // Defensive fallback only — every known StripeAccountStatus is handled above.
  return {
    severity: 'info',
    title: 'Account Under Review',
    message:
      "Your payment account is being reviewed. We'll update you when it's ready.",
    action: 'WAIT'
  };
}
